import rollBtnBackground from "@/images/roll-button.webp"
import { XMarkIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/solid"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData } from "@/store/globalSlice"
import { useCallback, useEffect, useState } from "react"
import {
  getMinifiedSituation,
  getUserCreds,
  isMobile,
  socket,
} from "@/utils/utils"
import { images, minBidDifference } from "@/utils/data"
import { getCurrentPlayer } from "@/utils/gameHelpers"
import ButtonWithBackground from "@/Components/ButtonWithBackground"
import ModalButton from "@/Components/ModalButton"
import Money from "@/Components/Money"
import TradeMoneySlider from "@/Pages/Trade/TradeMoneySlider"
import NumberDialer from "@/Components/NumberDialer"
import classNames from "classnames"
import PropertySmallCard from "@/Pages/Game/PropertySmallCard"
import PropertyBigCard from "@/Components/PropertyBigCard"
import { mapDesigns } from "@mono/mapDesigns"
import StaticTimer from "@/Components/StaticTimer"
import { getMaxAllowedAuctionBid } from "@mono/constants"
import { useGameTimings } from "@/Hooks/useGameTimings"
import { playAuctionPlacedSound } from "@/utils/audioManager"
import { gameRoles } from "@/utils/gameRoles"
import Modal from "@/Components/Modal"
import { useTimerTickSound } from "@/Hooks/useTimerTickSound"
import { useGameTimer } from "@/utils/gameTimers"

const HiddenAuctionModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  // One of the few callers that needs the seconds for something other than
  // drawing them — the modal takes itself off screen on the last of them — so
  // this one does subscribe to the tick and re-render with it.
  const auctionTimeLeft = useGameTimer("auctionTimeLeft")
  const { auctionTotalTime } = useGameTimings()
  const [myBidValue, setMyBidValue] = useState(10)
  // Separate text state for the input so it can be emptied while typing
  // (a number-only state can never represent an in-progress empty field).
  const [bidText, setBidText] = useState("10")
  const [hasBid, setHasBid] = useState(false)
  // On mobile the slider is replaced by a phone-style dialer. Until the first
  // key is pressed the field still holds the default bid, so that first digit
  // replaces it instead of being appended to it.
  const mobile = isMobile()
  const [dialerTouched, setDialerTouched] = useState(false)

  const players = myGame?.situation?.players
  const currentPlayer = getCurrentPlayer(myGame as IGameDetails)
  const highestBidder = players?.find(
    (p) =>
      p.auctionData?.bid ===
      Math.max(...players.map((p) => p.auctionData?.bid || 0))
  )
  // The auction strategist can go 100 over his cash, since he pays 100 less.
  const maxAllowedBid = getMaxAllowedAuctionBid(currentPlayer)
  const hasNoMoney = maxAllowedBid <= 0
  // Typing past what the player can afford clamps silently, so the readout
  // calls out when the amount shown is the ceiling rather than what was typed.
  const atMaxBid = maxAllowedBid > 0 && myBidValue >= maxAllowedBid

  // Get the property being auctioned
  const map = mapDesigns.find(
    (map: IMapDesign) => map.id === myGame?.settings.mapId
  )
  const auctionProperty = map?.cells.find(
    (cell: ICell) =>
      cell.order === myGame?.situation?.activeAuctionPropertyOrder
  )
  const propertyGroup = map?.groups.find(
    (group: IPropertyGroup) =>
      group.id === auctionProperty?.propertyDetails?.groupId
  )

  const handleClose = () => {
    dispatch(
      setModalData({
        hiddenAuction: { visible: false },
      })
    )
  }
  useEffect(() => {
    setHasBid(false)
    setMyBidValue(10)
    setBidText("10")
    setDialerTouched(false)
  }, [myGame?.situation?.activeAuctionPropertyOrder])

  // Keep the input text in sync when the value changes elsewhere (e.g. slider),
  // but never overwrite an intentionally-emptied field.
  useEffect(() => {
    setBidText((prev) => {
      if (prev === "") return prev
      return parseInt(prev) !== myBidValue ? String(myBidValue) : prev
    })
  }, [myBidValue])

  // Real players with no money can't bid anyway, so their 0-bid is sent
  // automatically shortly after the auction starts (bots are handled server-side).
  useEffect(() => {
    if (!hasNoMoney || hasBid) return
    if (myGame?.situation?.activeAuctionPropertyOrder == null) return
    if (!currentPlayer || currentPlayer.isBankrupt) return
    const roomid = myGame.id
    const playerOrder = currentPlayer.order
    const timeout = setTimeout(() => {
      socket.emit("auction.response", {
        roomid,
        bidValue: 0,
        playerOrder,
      })
      setMyBidValue(0)
      setBidText("0")
      setHasBid(true)
    }, 3500)
    return () => clearTimeout(timeout)
  }, [
    hasNoMoney,
    hasBid,
    myGame?.id,
    myGame?.situation?.activeAuctionPropertyOrder,
    currentPlayer?.order,
    currentPlayer?.isBankrupt,
  ])

  // Ticks only while the bid is still ours to make. Once it is in — or once we
  // are too broke to make one and it will be sent as 0 for us — the clock is no
  // longer counting down on anything we can do, so it goes quiet even though the
  // auction runs on. Mirrors the conditions the modal itself renders under,
  // since this sits above those early returns.
  useTimerTickSound(
    !hasBid &&
      !hasNoMoney &&
      myGame?.situation?.activeAuctionPropertyOrder != null &&
      (auctionTimeLeft ?? 0) > 1 &&
      !!currentPlayer &&
      !currentPlayer.isBankrupt,
    "auctionTimeLeft"
  )

  if (myGame?.situation?.activeAuctionPropertyOrder === null) return null
  const handleClickOnBid = () => {
    socket.emit("auction.response", {
      roomid: myGame!.id,
      bidValue: myBidValue,
      playerOrder: currentPlayer!.order,
    })
    setHasBid(true)
    // playAuctionPlacedSound()
  }

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === "") {
      // Allow the field to be fully cleared; treat an empty field as 0.
      setBidText("")
      setMyBidValue(0)
      return
    }
    const value = parseInt(raw)
    if (isNaN(value)) return
    // Never let the bid exceed the player's own money (or go below 0). The text
    // itself is clamped so an over-budget number can't even be shown.
    const clamped = Math.min(Math.max(0, value), maxAllowedBid)
    setBidText(String(clamped))
    setMyBidValue(clamped)
  }
  // Dialer (mobile) — digits are appended to the current amount and the result
  // is clamped to what the player can actually afford.
  const setBid = (value: number) => {
    const clamped = Math.min(Math.max(0, value), maxAllowedBid)
    setMyBidValue(clamped)
    setBidText(String(clamped))
  }
  const handleDialerDigit = (digit: number) => {
    const current = dialerTouched ? bidText : ""
    const base = current === "0" ? "" : current
    const next = base + digit
    // Nine digits is already far past any reachable bid; stop growing the text
    // so it can't overflow the display.
    if (next.length > 9) return
    setDialerTouched(true)
    setBid(parseInt(next))
  }
  const handleDialerBackspace = () => {
    const next = (dialerTouched ? bidText : "").slice(0, -1)
    setDialerTouched(true)
    setBid(next === "" ? 0 : parseInt(next))
  }
  const handleDialerReset = () => {
    setDialerTouched(true)
    setBid(0)
  }
  const handleNumberInputBlur = () => {
    if (bidText === "") setBidText(String(myBidValue))
  }
  const handleNumberInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key !== "Enter") return
    e.preventDefault()
    if (hasBid) return
    if (bidText === "") setBidText(String(myBidValue))
    handleClickOnBid()
  }
  const player = myGame?.situation?.players?.find(
    (player) => player.id === getUserCreds().email
  )
  if (myGame?.situation?.activeAuctionPropertyOrder === null) return null
  if (auctionTimeLeft === 1 || auctionTimeLeft === 0) return null
  if (!player || player?.isBankrupt) {
    return null
  }
  return (
    <Modal
      className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] max-w-3xl h-fit z-[100]"
      title="Hidden Auction"
      subtitle="Place your secret bid"
      contentClassName="px-8 pb-8"
    >
      {/* Timer — top right corner */}
      <div className="absolute top-5 right-6 z-20">
        <StaticTimer
          totalTime={auctionTotalTime - 1}
          currentTime={(auctionTimeLeft ?? 0) - 1}
          size="lg"
          labelText="Time Left"
          className="text-white font-semibold"
        />
      </div>

      <div className="grid  grid-cols-[auto_1fr] items-start gap-4 h-full">
        {/* Left: Property Big Card + price */}
        {auctionProperty && (
          <div className="w-[18rem] flex flex-col items-center gap-2">
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs uppercase tracking-widest text-yellow-200/80 font-poppins">
                Property Value
              </span>
              <Money
                money={auctionProperty.propertyDetails?.price || 0}
                className="gap-2 font-poppins text-3xl font-extrabold text-white"
                iconClass="h-7"
              />
            </div>
            <PropertyBigCard
              label="auctioned"
              cell={auctionProperty}
              size="auction-modal"
            />
          </div>
        )}

        {/* Right: Bid Section (bottom aligned) */}
        <div className="w-full h-full flex flex-col justify-end space-y-6 self-end">
          {hasBid ? (
            <div className="text-center bg-gradient-to-r rounded-xl p-8 flex flex-col items-center">
              <div className="text-3xl md:text-4xl font-poppins text-white font-semibold mb-4">
                Your Bid
              </div>
              <Money
                money={myBidValue}
                className="text-5xl md:text-6xl font-extrabold font-poppins text-white gap-3"
                iconClass="h-10 md:h-12"
              />
              {hasNoMoney && (
                <div className="mt-4 text-sm md:text-base text-red-300 font-poppins">
                  You have no money, so your bid was automatically set to 0.
                </div>
              )}
              <div className="mt-6 text-base md:text-lg text-yellow-200/90 font-poppins">
                <span className="inline-flex items-center gap-2">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                  Waiting for others to bid...
                </span>
              </div>
            </div>
          ) : hasNoMoney ? (
            <div className="text-center bg-red-500/10 border border-red-500/40 rounded-xl p-8 flex flex-col items-center">

              <div className="text-xl md:text-2xl font-poppins text-red-300 font-semibold">
                You have no money!
              </div>
              <div className="mt-3 text-sm md:text-base text-white/80 font-poppins">
                Your bid will be automatically set to 0 in a few seconds.
              </div>
            </div>
          ) : (
            <div className="space-y-6 sm:space-y-3">
              {/* Bid entry — dialer on mobile, slider everywhere else */}
              {mobile ? (
                // The keypad cap is set by the thumb, not by the panel: the
                // horizontal room here is free, so the keys take as much of it
                // as they can without the pad sprawling.
                <div className="mx-auto flex w-full max-w-[18rem] flex-col gap-[clamp(0.375rem,1.4vh,0.75rem)]">
                  {/* Current bid readout (no native input, so the OS keyboard
                      never covers the board). The default bid stays dimmed
                      until a key is pressed, since that first digit replaces
                      it rather than being appended to it. */}
                  <div
                    className="rounded-xl border border-amber-400/30 bg-black/40 px-3 py-[clamp(0.25rem,1vh,0.6rem)] shadow-[inset_0_2px_12px_rgba(0,0,0,0.6)]"
                    data-testid="auction.dialer-display"
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <Money
                        money={myBidValue}
                        className={classNames(
                          "gap-2 font-poppins font-extrabold tabular-nums leading-none text-[length:clamp(1.5rem,4.2vh,2.4rem)]",
                          dialerTouched ? "text-white" : "text-white/50"
                        )}
                        iconClass="h-[3vh]"
                      />
                      {/* Text caret — makes the readout read as a field being
                          typed into rather than a static label. */}
                      <span className="w-[2px] h-[clamp(1.1rem,3.2vh,1.8rem)] rounded-full bg-amber-300 animate-pulse" />
                    </div>
                    <div className="mt-[clamp(0.15rem,0.6vh,0.4rem)] flex items-center justify-center gap-1 font-poppins text-[length:clamp(0.6rem,1.7vh,0.85rem)]">
                      <span
                        className={
                          atMaxBid
                            ? "text-amber-300 font-semibold"
                            : "text-white/40"
                        }
                      >
                        Max
                      </span>
                      <Money
                        testId="auction.money-slider-max"
                        money={maxAllowedBid}
                        className={classNames(
                          "gap-1 font-semibold tabular-nums",
                          atMaxBid ? "text-amber-300" : "text-white/40"
                        )}
                        iconClass="h-[1.8vh]"
                      />
                    </div>
                  </div>
                  <NumberDialer
                    className="w-full"
                    onDigit={handleDialerDigit}
                    onBackspace={handleDialerBackspace}
                    onReset={handleDialerReset}
                    disabled={hasBid}
                  />
                </div>
              ) : (
                <div className="bg-gradient-to-br rounded-xl p-6">
                  <div className="space-y-4">
                    <TradeMoneySlider
                      className="w-full"
                      max={maxAllowedBid}
                      value={myBidValue}
                      setValue={setMyBidValue}
                      min={0}
                      minTestId="auction.money-slider-min"
                      maxTestId="auction.money-slider-max"
                      disabled={hasBid}
                    />

                    <div className="flex items-center gap-4">
                      <input
                        type="number"
                        value={bidText}
                        onChange={handleNumberInputChange}
                        onBlur={handleNumberInputBlur}
                        onKeyDown={handleNumberInputKeyDown}
                        min={0}
                        max={maxAllowedBid}
                        className="flex-1 px-3 py-2 bg-gray-800/50 border border-gray-600 rounded-lg text-lg text-white font-poppins font-semibold focus:outline-none focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/20 text-center"
                        disabled={hasBid}
                        placeholder="Enter bid amount"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Bid Button */}
              <div className="flex justify-center">
                <ModalButton
                  color="yellow"
                  disabled={hasBid}
                  onClick={handleClickOnBid}
                  className={
                    hasBid ? "grayscale opacity-60 cursor-not-allowed" : ""
                  }
                  data-testid="auction.bid-btn"
                >
                  <div className="flex flex-row items-center justify-center gap-3 w-full">
                    <i className="fa-gavel fa-solid text-white fa-xl"></i>
                    <span className="text-base text-white/90 font-poppins font-semibold tracking-wide uppercase">
                      Place Bid
                    </span>
                  </div>
                </ModalButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default HiddenAuctionModal
